import * as React from 'react';
import styled, { css, ThemedStyledProps, DefaultTheme } from 'styled-components';
import { IToolbarProps } from '../types';
import { Icon } from '../../icon';
import { useChooseRef, useKeyStrokeListener } from '../../../utils';
import { IToolbarItemProps } from './types';
import { selectThemeSemantics } from '../../../styling/theming';
import { Typography } from '../../typography';

interface IToolbarItemBaseProps extends Pick<IToolbarProps, 'iconPosition' | 'height'>, IToolbarItemProps {
    itemIndex: number;
    windowWidth?: number;
    onItemLoad?(itemPosition: Pick<ClientRect, 'left' | 'width'>, itemIndex: number): void;
    onItemInteracted?(): void;
}

const StyledIcon = styled(Icon).attrs({
    className: String('ap-toolbar-icon')
}) <{ $isMarginRequired: boolean; }>`
    margin-right: ${({ $isMarginRequired, theme: { semantics: { gap } } }) => ($isMarginRequired ? `${gap.l}` : `${gap.none}`)};
    fill: ${({ theme: { toolbar } }) => toolbar.colors.menuIconColor};
`;

const MenuTitle = styled(Typography.Small).attrs({
    className: String('ap-toolbar-label')
})`
    color: ${({ theme: { toolbar } }) => toolbar.colors.menuTitleColor};
`;

const ToolbarItemBase = React.memo((props: IToolbarItemBaseProps) => {
    const {
        title, className, iconName, iconSize, itemIndex, elementRef, windowWidth,
        iconPosition, tooltip, onRender, onClick, onItemLoad, onItemInteracted
    } = props;

    const rootElementRef = useChooseRef(elementRef);

    React.useEffect(() => {
        const { current } = rootElementRef;
        if (!current || onItemLoad === undefined) {
            return;
        }
        const { left, width } = current.getBoundingClientRect();
        onItemLoad({ left, width }, itemIndex);
    }, [windowWidth, iconPosition, itemIndex, onItemLoad, rootElementRef]);

    const _onClick = React.useCallback((ev) => {
        if (onClick !== undefined) {
            onClick(ev);
        }
        if (onItemInteracted === undefined) {
            return;
        }
        onItemInteracted();
    }, [onClick, onItemInteracted]);

    const { subscribe, unsubscribe } = useKeyStrokeListener(rootElementRef);

    React.useEffect(() => {
        subscribe(_onClick, 'Enter');
        return unsubscribe;
    }, [_onClick, subscribe, unsubscribe]);

    if (onRender !== undefined) {
        return <span className={className} ref={rootElementRef}>{onRender(props)}</span>;
    }

    return (
        <span className={className} onClick={_onClick} ref={rootElementRef} title={tooltip ?? title} tabIndex={0}>
            {iconName ? <StyledIcon iconName={iconName} iconSize={iconSize} $isMarginRequired={!!title && iconPosition !== 'top'} /> : null}
            <MenuTitle>{title}</MenuTitle>
        </span>
    );
});

ToolbarItemBase.displayName = 'ToolbarItem';

const hoveredStyle = css`
    &:hover {
        box-shadow: ${({ theme: { toolbar } }) => toolbar.composites.boxShadowOnItemHover};
    }
`;

function getPadding(componentProps: ThemedStyledProps<IToolbarItemBaseProps, DefaultTheme>) {
    const { onRender, iconPosition, title } = componentProps;
    if (onRender !== undefined) {
        return '';
    }

    if (iconPosition === 'left') {
        return selectThemeSemantics(({ gap }) => `${gap.none} ${gap.xl}`);
    }

    return selectThemeSemantics(({ gap }) => `${title ? `${gap.l}` : `${gap.none}`} ${gap.xl}`);
}

export const ToolbarItem = styled(ToolbarItemBase).attrs({
    className: String('ap-toolbar-item')
})`
    outline-color:#0077D9;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${getPadding};
    cursor: pointer;
    box-sizing: border-box;
    flex-shrink: 0;
    flex-grow: 0;
    user-select: none;
    height: ${({ height }) => (height ? `${height}px` : '40px')};
    flex-direction: ${({ iconPosition }) => (iconPosition === 'top' ? 'column' : 'row')};
    ${({ onRender }) => (onRender ? '' : hoveredStyle)};
`;
